import React, { memo, useCallback, useMemo, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Trash from '@atlaskit/icon/core/migration/delete--trash';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type {
	SelectValue,
	AsyncLoadOptions,
	OnChangeWithData,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { Picker } from '@atlassian/jira-jql-builder-basic-picker/src/ui/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import PickerTrigger from '../../common/ui/picker-trigger/index.tsx';
import { SortDirectionIcon } from '../../common/ui/sort-direction-icon/index.tsx';
import { FALLBACK_SORT_DIRECTION } from '../../constants.tsx';
import type { OrderByField } from '../../types.tsx';
import { toggleDirection } from '../../utils.tsx';
import messages from './messages.tsx';
import { OrderByPicker as OrderByPickerNew } from './main.tsx';

type Props = {
	field: OrderByField;
	fieldIndex: number;
	onRemove: (fieldIndex: number) => void;
	onLoadOptions: AsyncLoadOptions;
	onChange: (fieldIndex: number, selectedField: OrderByField) => void;
};

const OrderByPickerOld = ({ field, fieldIndex, onChange, onRemove, onLoadOptions }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const pickerValue = useMemo<SelectValue>(
		() => [
			{
				optionType: 'option',
				value: field.jqlTerm,
				label: field.displayText,
			},
		],
		[field.displayText, field.jqlTerm],
	);

	const handleRemoveFieldButtonClick = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent, analyticsEvent: UIAnalyticsEvent) => {
			e.preventDefault();
			fireUIAnalytics(analyticsEvent, 'orderBySelectRemoveFieldClick');
			onRemove(fieldIndex);
		},
		[fieldIndex, onRemove],
	);

	const handleTriggerClick = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			e.preventDefault();

			setIsOpen((currentisOpen) => {
				const newOpenState = !currentisOpen;

				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: 'dropdownMenu',
					actionSubjectId: 'orderBySelectFieldPicker',
					attributes: {
						isOpen: newOpenState,
					},
				});

				return newOpenState;
			});
		},
		[createAnalyticsEvent],
	);

	const handlePickerCloseClick = useCallback(
		(
			e:
				| Event
				| KeyboardEvent
				| React.MouseEvent<HTMLElement>
				| React.KeyboardEvent<HTMLDivElement>,
		) => {
			e.preventDefault();
			setIsOpen(false);
		},
		[],
	);

	const handleSortDirectionClick = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent, analyticsEvent: UIAnalyticsEvent) => {
			e.preventDefault();

			fireUIAnalytics(analyticsEvent, 'orderBySelectSortDirectionClick');

			/**
			 * It is expected that some order by fields in JQL dont have an explicit direction,
			 * E.g. `ORDER BY project` instead of `ORDER BY project ASC`
			 * In these cases if the direction is toggled, we add an explicit direction
			 */
			const updatedField = {
				...field,
				direction: field.direction ? toggleDirection(field.direction) : FALLBACK_SORT_DIRECTION,
			};

			onChange(fieldIndex, updatedField);
		},
		[field, fieldIndex, onChange],
	);

	const handleFieldChanged: OnChangeWithData = useCallback(
		(newValue, actionMeta) => {
			// @ts-expect-error TS inference not working with the SelectedValue type
			// despite explicit checks it always still thinks it might be an array or null
			// not sure of the root cause whether type defs in
			// jira/src/packages/jql-builder/basic-picker/src/common/types.tsx or TS issue at
			// the platform level
			if (newValue === null || Array.isArray(newValue) || !newValue.value || !newValue.label) {
				return null;
			}

			if (actionMeta.action === 'select-option') {
				const updatedField = {
					// @ts-expect-error see above
					displayText: newValue.label,
					// @ts-expect-error see above
					jqlTerm: newValue.value,
					direction: field.direction,
				};
				onChange(fieldIndex, updatedField);

				setIsOpen(false);

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'changed',
						actionSubject: 'selectOption',
					}),
					'orderByFieldPicker',
				);
			}
		},
		[createAnalyticsEvent, field.direction, fieldIndex, onChange],
	);

	const onPickerTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<PickerTrigger
				triggerProps={triggerProps}
				displayText={field.displayText}
				onClick={handleTriggerClick}
				isOpen={isOpen}
				ariaLabel={formatMessage(messages.pickerLabel, { field: field.displayText })}
				shouldFitContainer
			/>
		),
		[field.displayText, formatMessage, handleTriggerClick, isOpen],
	);

	return (
		<Flex>
			<Box xcss={pickerContainerStyles}>
				<Picker
					isMulti={false}
					isOpen={isOpen}
					id={`order-by-select-field-${fieldIndex}`}
					value={pickerValue}
					onTrigger={onPickerTrigger}
					onClose={handlePickerCloseClick}
					onChange={handleFieldChanged}
					onLoadOptions={onLoadOptions}
					shouldRenderToParent
				/>
			</Box>

			<Box xcss={sortButtonContainerStyles}>
				<Tooltip position="bottom" content={formatMessage(messages.sortButtonTooltip)}>
					{(tooltipProps) => (
						<Button
							{...tooltipProps}
							onClick={handleSortDirectionClick}
							iconAfter={
								<SortDirectionIcon
									fieldDisplayText={field.displayText}
									fieldSortDirection={field.direction}
								/>
							}
						/>
					)}
				</Tooltip>
			</Box>

			<Box xcss={clearButtonContainerStyles}>
				<Tooltip position="bottom" content={formatMessage(messages.clearButtonTooltip)}>
					{(tooltipProps) => (
						<Button
							{...tooltipProps}
							appearance="subtle"
							onClick={handleRemoveFieldButtonClick}
							iconAfter={
								<Box as="span" xcss={[iconButtonStyles]}>
									{}
									<Trash
										LEGACY_size="small"
										label={formatMessage(messages.clearButtonLabel, {
											field: field.displayText,
										})}
									/>
								</Box>
							}
						/>
					)}
				</Tooltip>
			</Box>
		</Flex>
	);
};

const OrderByPicker = componentWithFG(
	'jql_builder_lazy_picker_to_entrypoint',
	OrderByPickerNew,
	OrderByPickerOld,
);

export default memo(OrderByPicker);

const sortButtonContainerStyles = xcss({ flex: 'none', marginLeft: 'space.075' });
const clearButtonContainerStyles = xcss({ flex: 'none', marginLeft: 'space.150' });

/**
 * There is a bug with the new Atlaskit IconButton where occasionally the onClick handler is not
 * triggered, when wrapped by a tooltip - https://atlassian.slack.com/archives/CFJ9DU39U/p1718597982581429
 * Instead, using a legacy button but making it appear 32px wide so that it looks like the new icon
 * button. Any narrower and it becomes an a11y issue having such a small click target
 */
const iconButtonStyles = xcss({
	display: 'inline-block',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
});

const pickerContainerStyles = xcss({
	flexGrow: '1',
	minWidth: '0',
});
